/* import __COLOCATED_TEMPLATE__ from './sq-table.hbs'; */
/**
Use `sq-table` to create a responsive table! It uses default table layout, which distributes extra space based on column contents.
The table will scroll horizontally when the viewport becomes too small to render the whole table.

### Contextual Components

| Description | Contextual Component |
| --- | --- |
| Header | [sq-table/header](./sq-table/header.html) |
| Header -> Header Cell | [sq-table/header-cell](./sq-table/header-cell.html) |
| Body | [sq-table/body](./sq-table/body.html) |
| Body -> Row | [sq-table/row](./sq-table/row.html) |
| Body -> Row -> Cell | [sq-table/cell](./sq-table/cell.html) |

```example
@title Basic Example
@controller-properties
{
  rows: [
    {
      name: 'Liz Hagearty',
      ldap: 'hagearty',
      email: 'hagearty@squareup.com'
    },
    {
      name: 'Matt Wright',
      ldap: 'mattwright',
      email: 'mattwright@squareup.com'
    },
    {
      name: 'Jessica Biggs',
      ldap: 'jbiggs',
      email: 'jbiggs@squareup.com'
    }
  ]
}

@hbs
{{#sq-table as |table|}}
  {{#table.header as |header|}}
    {{#header.cell}}
      Name
    {{/header.cell}}
    {{#header.cell}}
      LDAP
    {{/header.cell}}
    {{#header.cell}}
      Email
    {{/header.cell}}
  {{/table.header}}

  {{#table.body as |body|}}
    {{#each rows as |rowData|}}
      {{#body.row as |row|}}
        {{#row.cell}}
          {{rowData.name}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.ldap}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.email}}
        {{/row.cell}}
      {{/body.row}}
    {{/each}}
  {{/table.body}}
{{/sq-table}}
```

```example
@title Selectable Row
@description Pass a <code>selectAction</code> to the yielded <code>body.row</code> to get selectable hover styling and have that action called on row click.

@controller-properties
{
  rows: [
    {
      name: 'Liz Hagearty',
      ldap: 'hagearty',
      email: 'hagearty@squareup.com'
    },
    {
      name: 'Matt Wright',
      ldap: 'mattwright',
      email: 'mattwright@squareup.com'
    },
    {
      name: 'Jessica Biggs',
      ldap: 'jbiggs',
      email: 'jbiggs@squareup.com'
    }
  ],

  actions: {
    select(name) {
      this.set('selectedPerson', name);
    }
  }
}

@hbs
<p>
  <strong>Selected row:</strong> {{selectedPerson}}
</p>

{{#sq-table as |table|}}
  {{#table.header as |header|}}
    {{#header.cell}}
      Name
    {{/header.cell}}
    {{#header.cell}}
      LDAP
    {{/header.cell}}
    {{#header.cell}}
      Email
    {{/header.cell}}
  {{/table.header}}

  {{#table.body as |body|}}
    {{#each rows as |rowData|}}
      {{#body.row selectAction=(action "select" rowData.name)
                  as |row|}}
        {{#row.cell}}
          {{rowData.name}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.ldap}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.email}}
        {{/row.cell}}
      {{/body.row}}
    {{/each}}
  {{/table.body}}
{{/sq-table}}
```

```example
@title Column sizing
@description Each column's width is the size of its contents, with a max width of 600px. The table will scroll horizontally if needed.
@controller-properties
{
  rows: [
    {
      name: 'Liz Hagearty',
      ldap: 'hagearty',
      email: 'hagearty@squareup.com',
      fixed: 'This is a decent size string!'
    },
    {
      name: 'Matt Wright',
      ldap: 'mattwright',
      email: 'mattwright@squareup.com',
      fixed: 'Wow there are so many words in this string, look how large this column is'
    },
    {
      name: 'Jessica Biggs',
      ldap: 'jbiggs',
      email: 'jbiggs@squareup.com',
      fixed: 'This column has even more words! Look at all these words! This text is going to overflow'
    }
  ]
}

@hbs
{{#sq-table as |table|}}
  {{#table.header as |header|}}
    {{#header.cell}}Column with long text{{/header.cell}}
    {{#header.cell}}Name{{/header.cell}}
    {{#header.cell}}LDAP{{/header.cell}}
    {{#header.cell}}Email{{/header.cell}}
  {{/table.header}}

  {{#table.body as |body|}}
    {{#each rows as |rowData|}}
      {{#body.row as |row|}}
        {{#row.cell}}
          {{rowData.fixed}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.name}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.ldap}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.email}}
        {{/row.cell}}
      {{/body.row}}
    {{/each}}
  {{/table.body}}
{{/sq-table}}
```

```example
@title Sortable
@description Using <code>sq-sorter</code> with <code>sq-table</code> enables sorting behavior
@controller-properties
{
  rows: [
    {
      name: 'Liz Hagearty',
      ldap: 'hagearty',
      email: 'hagearty@squareup.com'
    },
    {
      name: 'Matt Wright',
      ldap: 'mattwright',
      email: 'mattwright@squareup.com'
    },
    {
      name: 'Jessica Biggs',
      ldap: 'jbiggs',
      email: 'jbiggs@squareup.com'
    }
  ]
}

@hbs
{{#sq-sorter content=rows
             sortKey="name"
             as |sorter|}}
  {{#sq-table as |table|}}
    {{#table.header as |header|}}
      {{#header.cell}}
        {{#sorter.button sortKey="name"}}
          Name
        {{/sorter.button}}
      {{/header.cell}}
      {{#header.cell}}
        {{#sorter.button sortKey="ldap"}}
          LDAP
        {{/sorter.button}}
      {{/header.cell}}
      {{#header.cell}}
        {{#sorter.button sortKey="email"}}
          Email
        {{/sorter.button}}
      {{/header.cell}}
    {{/table.header}}

    {{#table.body as |body|}}
      {{#each sorter.sortedContent as |rowData|}}
        {{#body.row as |row|}}
          {{#row.cell}}
            {{rowData.name}}
          {{/row.cell}}
          {{#row.cell}}
            {{rowData.ldap}}
          {{/row.cell}}
          {{#row.cell}}
            {{rowData.email}}
          {{/row.cell}}
        {{/body.row}}
      {{/each}}
    {{/table.body}}
  {{/sq-table}}
{{/sq-sorter}}
```

```example
@title Fixed Left Column
@description Set <code>isFixedLeft=true</code> on the yielded <code>header-cell</code> and <code>cell</code> components to fix the leftmost column to the left.
@controller-properties
{
  rows: [
    {
      name: 'Liz Hagearty',
      ldap: 'hagearty',
      email: 'hagearty@squareup.com'
    },
    {
      name: 'Matt Wright',
      ldap: 'mattwright',
      email: 'mattwright@squareup.com'
    },
    {
      name: 'Jessica Biggs',
      ldap: 'jbiggs',
      email: 'jbiggs@squareup.com'
    }
  ]
}

@hbs
{{#sq-table as |table|}}
  {{#table.header as |header|}}
    {{#header.cell isFixedLeft=true}}
      Name
    {{/header.cell}}
    {{#header.cell}}
      LDAP
    {{/header.cell}}
    {{#header.cell}}
      Email
    {{/header.cell}}
  {{/table.header}}

  {{#table.body as |body|}}
    {{#each rows as |rowData|}}
      {{#body.row as |row|}}
        {{#row.cell isFixedLeft=true}}
          {{rowData.name}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.ldap}}
        {{/row.cell}}
        {{#row.cell}}
          {{rowData.email}}
        {{/row.cell}}
      {{/body.row}}
    {{/each}}
  {{/table.body}}
{{/sq-table}}
```

### Parameters
@param {Boolean} [useFixedLayout=false] - Use the fixed table rendering algorithm to evenly distribute column widths across all columns, regardless of content. Useful for dynamic/lazy loaded content, to avoid column widths changing as content is loaded.
*/

import Component from '@ember/component';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['sq-table__container'],
  classNameBindings: ['isScrollableRight:sq-table__container--is-scrollable-right', 'isScrollableLeft:sq-table__container--is-scrollable-left'],
  // Applies `.sq-table__table--fixed-layout` to the inner table
  useFixedLayout: false,
  didInsertElement(...args) {
    this._super(...args);
    this.boundUpdateScrollableStatus = () => this.updateScrollableStatus();
    window.addEventListener('resize', this.boundUpdateScrollableStatus);
    this.element.addEventListener('scroll', this.boundUpdateScrollableStatus);
  },
  didRender(...args) {
    this._super(...args);
    this.updateScrollableStatus();
  },
  willDestroyElement(...args) {
    this._super(...args);
    window.removeEventListener('resize', this.boundUpdateScrollableStatus);
    this.element.removeEventListener('scroll', this.boundUpdateScrollableStatus);
  },
  updateScrollableStatus() {
    const {
      scrollWidth,
      clientWidth,
      scrollLeft
    } = this.element;
    const scrollRight = scrollWidth - clientWidth - scrollLeft;
    this.setProperties({
      isScrollableRight: scrollRight > 0,
      isScrollableLeft: scrollLeft > 0
    });
  }
});